import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  Stethoscope,
  BrainCircuit,
  Home,
  Menu,
  X,
} from "lucide-react";
import logo from "../assets/favicro.png";

const Navbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navButtons = [
    {
      label: "Home",
      path: "/doctor-dashboard",
      icon: <Home className="w-4 h-4 text-blue-500" />,
    },
    {
      label: "Add Patient",
      path: "/add_patient",
      icon: <BrainCircuit className="w-4 h-4 text-green-600" />,
    },
    {
      label: "Lab Manager",
      path: "/add_lab",
      icon: <BrainCircuit className="w-4 h-4 text-purple-600" />,
    },
    {
      label: "Pharmacy Manager",
      path: "/add_pharmacy",
      icon: <BrainCircuit className="w-4 h-4 text-pink-600" />,
    },
    {
      label: "Ambulance Manager",
      path: "/add_ambulance",
      icon: <BrainCircuit className="w-4 h-4 text-red-600" />,
    },
    {
      label: "Nurse Manager",
      path: "/nurse_manager",
      icon: <span className="text-base">🧑‍⚕️</span>,
    },
    {
      label: "Medicine Info",
      path: "/medicine_chatbot",
      icon: <Stethoscope className="w-4 h-4 text-blue-500" />,
    },
    {
      label: "Team Doctor",
      path: "/team_manager",
      icon: <Stethoscope className="w-4 h-4 text-orange-500" />,
    }
  ];

  const currentPath = location.pathname;
  const visibleButtons = navButtons.filter((btn) => btn.path !== currentPath);
  const showProfileButton = currentPath !== "/profile";

  return (
    <nav className="bg-white shadow-md px-3 py-2 flex justify-between items-center fixed top-0 w-full z-50">
      {/* Logo */}
      <div className="flex items-center gap-1 text-xl font-bold text-blue-600">
        <img src={logo} alt="AICA Logo" className="w-6 h-6 object-contain" />
        <span>
          AICA<span className="text-gray-700"> | Doctor</span>
        </span>
      </div>

      {/* Desktop Nav Buttons */}
      <div className="hidden md:flex items-center gap-3">
        {visibleButtons.map((btn, index) => (
          <button
            key={index}
            onClick={() => navigate(btn.path)}
            className={`flex items-center gap-1 text-xs font-medium rounded-lg py-1.5 px-2 ${
              location.pathname === btn.path
                ? "text-blue-600 underline underline-offset-4"
                : "text-gray-700 hover:text-blue-600"
            }`}
          >
            <span className="w-4 h-4">{btn.icon}</span>
            <span className="hidden sm:inline">{btn.label}</span>
          </button>
        ))}

        {/* Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            className="flex items-center gap-1 text-gray-700 hover:text-blue-600 text-xs"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">
              {localStorage.getItem("doctor_name")}
            </span>
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg p-2 z-50">
              {showProfileButton && (
                <button
                  onClick={() => navigate("/profile")}
                  className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded"
                >
                  Profile
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-100 rounded"
              >
                <LogOut className="w-3 h-3" /> Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Hamburger Menu */}
      <div className="md:hidden">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="absolute top-14 left-0 w-full bg-white shadow-md border-t z-40 flex flex-col p-3 space-y-2 md:hidden">
          {visibleButtons.map((btn, index) => (
            <button
              key={index}
              onClick={() => {
                navigate(btn.path);
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 py-1.5"
            >
              <span className="w-4 h-4">{btn.icon}</span>
              {btn.label}
            </button>
          ))}

          {showProfileButton && (
            <button
              onClick={() => {
                navigate("/profile");
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 py-1.5"
            >
              <User className="w-4 h-4" /> Profile
            </button>
          )}
          <button
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
            className="flex items-center gap-2 text-xs text-red-600 hover:text-red-700 py-1.5"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
